import { Accordion, AccordionItem } from '@heroui/react';
import NextLink from 'next/link';

import { BlogPageLayout } from '../../components/blog/BlogPageLayout';
import { Section } from '../../components/layout/Storyblok/Section/Section';
import wysiwygStyles from '../../components/layout/Storyblok/Wysiwyg/Wysiwyg.module.scss';
import { CTASection } from '../../components/shared/CTASection';
import { FAQStructuredData } from '../../components/shared/FAQStructuredData';
import { KeyTakeaways } from '../../components/shared/KeyTakeaways';
import { Icon } from '../../components/various/Icon';
import { useSession } from '../../hooks/useSession';
import { SectionStoryblok } from '../../typings/storyblok';
import { getHeadingId } from '../../utils/storyblok';

const DOCUMENT_TITLE = 'invideo vs. DaVinci Resolve: Who Wins for Color-Critical vs. AI-Generated Video?';
const META_DESCRIPTION =
  "DaVinci Resolve wins decisively for color-critical work, since it's the industry-benchmark color grading tool used widely across professional film and…";
const PUBLISH_DATE = '2026-09-10 00:00';
const READ_TIME = '7';
const CANONICAL_PATH = '/blog/invideo-vs-davinci-resolve-color-vs-ai-generated';
const COVER_SRC = '/images/blog/invideo-vs-davinci-resolve-color-vs-ai-generated-cover.png';

const HERO_BLOK: SectionStoryblok = {
  _uid: 'static-invideo-vs-davinci-resolve-color-vs-ai-generated-hero',
  component: 'section',
  title: DOCUMENT_TITLE,
  titleTag: 'h1',
  text: 'DaVinci Resolve is the grading benchmark. invideo agent generates an original sequence from a script.',
  orientation: '',
  displaySocials: false,
  imageObjectFit: 'cover',
  image: {
    _uid: 'static-invideo-vs-davinci-resolve-color-vs-ai-generated-cover',
    id: null,
    alt: 'invideo vs. DaVinci Resolve: Who Wins for Color-Critical vs. AI-Generated Video?',
    name: '',
    focus: '',
    source: '',
    title: DOCUMENT_TITLE,
    filename: COVER_SRC,
    copyright: '',
    fieldtype: 'asset',
  },
};

const TOC_SECTIONS = [
  'Quick answer',
  'What each tool actually is',
  'Feature-by-feature comparison',
  'Where DaVinci Resolve wins',
  'Where invideo wins',
  'Pricing side by side',
  'The verdict',
  'Frequently asked questions',
] as const;

const FAQS = [
  {
    question: 'Does DaVinci Resolve have any AI video generation capability comparable to invideo agent?',
    answer:
      "No. DaVinci Resolve's AI tools, Magic Mask, Speed Warp, Face Refinement, noise reduction, all assist footage that has already been shot. None of them generate an original scene from a text description or a script, which is the core function of invideo agent.",
  },
  {
    question: "Is DaVinci Resolve's free version actually usable for professional color work?",
    answer:
      'Yes, genuinely. The free version includes full node-based primary and secondary color correction, LUT support, and HDR metadata handling with no watermark or time limit, capable of professional-grade work. The paid Studio license adds HDR grading in PQ/HLG, ACES 1.3 support, and GPU-accelerated tools for the most demanding finishing needs.',
  },
  {
    question: "Can invideo's tools replace DaVinci Resolve for color grading?",
    answer:
      "Not for color-critical finishing work. Invideo Editor includes color and grading tools for matching footage cut together from different sources, but it isn't built as a dedicated, node-based grading suite the way DaVinci Resolve is, and wouldn't be the right choice for feature-level color finishing.",
  },
  {
    question: 'Would a production ever use both tools together?',
    answer:
      "Yes, and this is a reasonable real workflow. A project could use invideo agent to generate original sequences or Invideo Editor to assemble footage, then bring the result into DaVinci Resolve for a dedicated, color-critical final grade, using each tool for the half of the job it's actually built for.",
  },
  {
    question: 'Which tool is cheaper overall?',
    answer:
      "It depends on the time horizon. DaVinci Resolve Studio's $295 one-time cost can be cheaper than a multi-year subscription if grading is the only need. invideo agent's $17/month entry plan and Invideo Editor's free timeline are cheaper for a project that needs to generate original video rather than grade existing footage, a job DaVinci Resolve isn't built for at all.",
  },
] as const;

export default function InvideoVsDavinciResolveColorVsAiGeneratedPage() {
  useSession();

  const tocLinks = TOC_SECTIONS.map((label) => ({
    label,
    url: `#${getHeadingId(label)}`,
  }));

  return (
    <>
      <FAQStructuredData faqs={[...FAQS]} />
      <BlogPageLayout
        documentTitle={DOCUMENT_TITLE}
        metaDescription={META_DESCRIPTION}
        canonicalPath={CANONICAL_PATH}
        ogImageUrl={COVER_SRC}
        ogImageAlt={DOCUMENT_TITLE}
        publishDate={PUBLISH_DATE}
        readTimeMinutes={READ_TIME}
        breadcrumbLabel="invideo vs DaVinci Resolve"
        tocLinks={tocLinks}
      >
        <Section blok={HERO_BLOK} />

        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-transparent p-6 sm:p-8">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex-shrink-0 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 p-2">
              <Icon icon="checkCircle" size={18} className="text-primary" />
            </div>
            <p className="font-sans text-base font-bold">Article summary</p>
          </div>
          <p className="pl-1 text-sm leading-relaxed text-foreground-600 sm:text-base">{META_DESCRIPTION}</p>
        </div>

        <div className={wysiwygStyles.wysiwyg}>
          <p>
            DaVinci Resolve wins decisively for color-critical work, since it's the industry-benchmark color grading
            tool used widely across professional film and television post-production, with a genuinely free version that
            already outperforms most paid alternatives. Invideo wins for AI-generated video, since DaVinci Resolve has
            no equivalent to generating an original sequence from a script, its AI tools assist footage that already
            exists. These aren't really competing claims, they're accurate descriptions of two tools built for different
            halves of a production.
          </p>

          <KeyTakeaways
            items={[
              "Choose DaVinci Resolve if precise, professional color grading is the actual priority, on footage that's already been shot.",
              'Choose invideo agent if the project starts from a script with no footage yet, and needs an original sequence planned and generated with consistency held automatically.',
              'Choose Invideo Editor if you want an AI video editor that handles assembly and agent-directed editing on real or generated footage, rather than a color-first finishing tool.',
            ]}
          />

          <h2 id={getHeadingId('Quick answer')}>Quick answer</h2>
          <ul>
            <li>
              Choose DaVinci Resolve if precise, professional color grading is the actual priority, on footage that's
              already been shot.
            </li>
            <li>
              Choose invideo agent if the project starts from a script with no footage yet, and needs an original
              sequence planned and generated with consistency held automatically.
            </li>
            <li>
              Choose Invideo Editor if you want an{' '}
              <a href="https://invideo.io/make/ai-video-editor/" target="_blank" rel="noopener noreferrer">
                AI video editor
              </a>{' '}
              that handles assembly and agent-directed editing on real or generated footage, rather than a color-first
              finishing tool.
            </li>
          </ul>
          <h2 id={getHeadingId('What each tool actually is')}>What each tool actually is</h2>
          <p>
            DaVinci Resolve is Blackmagic Design's all-in-one editing, color, VFX, and audio application, and its Color
            page is widely regarded as the benchmark for professional grading, node-based primary and secondary
            correction, LUT support, and HDR metadata handling, all included in a genuinely free version with no
            watermark and no time limit. The paid Studio license, a one-time $295 purchase rather than a subscription,
            adds HDR grading in PQ and HLG, ACES 1.3 support, stereoscopic 3D grading, and a set of AI-assisted tools,
            Magic Mask for object masking, Speed Warp for AI slow motion, Face Refinement, and GPU-accelerated noise
            reduction. Every one of these AI tools assists footage that's already been shot; none of them generate an
            original scene from a text description or a script.
          </p>
          <p>
            <a href="https://invideo.io/" target="_blank" rel="noopener noreferrer">
              invideo agent
            </a>{' '}
            solves a different problem entirely: a director describes a shot or hands over a script, and the agent plans
            a complete sequence, routing each shot to whichever of its 200+ integrated models fits that particular
            moment, including Veo 3.1, Sora 2, Kling AI, Seedance 2.5, Runway, PixVerse, Hailuo, WAN, Recraft, GPT Image
            2.0, and Nano Banana 2, while a persistent context engine holds characters, locations, and visual rules
            consistent across every generated shot. There's no footage to grade because the project starts before any
            exists.
          </p>
          <p>
            Invideo Editor sits between the two, handling the editing and assembly side once footage, generated or real,
            needs to be cut together: a professional timeline editor doing drag, trim, cut, and layer work while also
            taking agent instructions on the same timeline, with Agentic Assembly building a complete base cut from raw
            footage based on a plain-language description. It includes color and grading tools for matching footage cut
            together from different sources, though it isn't built to replace a dedicated color-critical grading suite
            for the most demanding finishing work.
          </p>
          <h2 id={getHeadingId('Feature-by-feature comparison')}>Feature-by-feature comparison</h2>
          <div className={wysiwygStyles.tableWrapper}>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>DaVinci Resolve</th>
                  <th>invideo agent</th>
                  <th>Invideo Editor</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Core strength</td>
                  <td>Professional color grading and finishing</td>
                  <td>Generating an original sequence from a script</td>
                  <td>Assembling and editing footage, real or generated</td>
                </tr>
                <tr>
                  <td>Requires footage to already exist</td>
                  <td>Yes</td>
                  <td>No</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Color grading depth</td>
                  <td>Industry-benchmark node-based grading, HDR, ACES 1.3 (Studio)</td>
                  <td>Not its function</td>
                  <td>Color and grading tools for matching cut footage</td>
                </tr>
                <tr>
                  <td>AI tools</td>
                  <td>Assist existing footage: masking, slow motion, noise reduction, face refinement</td>
                  <td>Generates and checks new footage against project consistency</td>
                  <td>Agentic Assembly, dubbing/localization with lip sync</td>
                </tr>
                <tr>
                  <td>Underlying generative video models</td>
                  <td>None</td>
                  <td>200+ integrated models</td>
                  <td>None; edits existing or generated footage</td>
                </tr>
                <tr>
                  <td>Free tier</td>
                  <td>Yes, genuinely full-featured, no watermark</td>
                  <td>No</td>
                  <td>Yes</td>
                </tr>
                <tr>
                  <td>Paid tier model</td>
                  <td>$295 one-time, perpetual license</td>
                  <td>Subscription, from $17/month</td>
                  <td>Free</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h2 id={getHeadingId('Where DaVinci Resolve wins')}>Where DaVinci Resolve wins</h2>
          <p>
            Color grading is simply its category, and it's the benchmark. A free version with full node-based grading,
            LUTs, and HDR metadata handling that already exceeds most paid competitors is a genuinely singular offer in
            this space, and the Studio license's HDR and ACES 1.3 support are what serious colorists and post houses
            actually need for feature-level finishing.
          </p>
          <p>
            One-time pricing with no recurring cost. The $295 Studio license is a perpetual purchase, not a
            subscription, which matters over any multi-year horizon compared with a recurring monthly fee.
          </p>
          <p>
            A genuinely all-in-one professional application. Editing, color, Fusion VFX, and Fairlight audio all live in
            one program, which real productions rely on to avoid round-tripping between separate specialized tools.
          </p>
          <h2 id={getHeadingId('Where invideo wins')}>Where invideo wins</h2>
          <p>
            invideo agent doesn't need footage to grade because it generates the footage itself. For a project starting
            from a script with nothing filmed, DaVinci Resolve has nothing to offer, since every one of its tools,
            including its AI features, works on video that already exists.
          </p>
          <p>
            Consistency is checked automatically across a fully generated project. invideo agent's context engine holds
            a character or environment consistent across every generated shot and checks new generations against the
            project's established rules, a concern that doesn't arise for a color and finishing tool.
          </p>
          <p>
            Invideo Editor assembles raw footage without a manual organizing pass. Its Agentic Assembly builds a base
            cut from unorganized takes based on plain-language direction, a capability outside DaVinci Resolve's editing
            toolset, which still requires an editor to organize and select footage manually before cutting.
          </p>
          <p>
            Dubbing and localization with lip sync preserved. Invideo Editor's translation and dubbing capability goes
            further than DaVinci Resolve's editing and color toolset offers for multilingual delivery.
          </p>
          <h2 id={getHeadingId('Pricing side by side')}>Pricing side by side</h2>
          <p>
            DaVinci Resolve's free version is fully capable with no watermark or meaningful feature restriction for most
            editing and grading work, and Studio is a one-time $295 purchase with lifetime updates included, no
            subscription at any point. invideo agent's plans start at $17/month with team and enterprise options, and
            Invideo Editor's timeline is free to use regardless of plan.
          </p>
          <h2 id={getHeadingId('The verdict')}>The verdict</h2>
          <p>
            The title's framing is the honest answer: these tools genuinely win at different things. DaVinci Resolve is
            the stronger choice, by a wide margin, for color-critical work on footage that already exists, its grading
            tools are the professional standard for a reason, and its free version alone outperforms most paid
            alternatives. Invideo is the stronger choice for AI-generated video, since DaVinci Resolve has no path from
            a script to an original sequence, only tools that refine footage once it's been shot. A production doing
            both, generating original sequences and finishing them with serious color work, would reasonably use invideo
            agent or Invideo Editor for the first half and DaVinci Resolve for the final grade.
          </p>
          <hr />
          <h2 id={getHeadingId('More invideo comparisons')}>More invideo comparisons</h2>
          <ul>
            <li>
              <NextLink href="/blog/invideo-vs-descript-script-driven-video-editing">invideo vs Descript</NextLink>
            </li>
            <li>
              <NextLink href="/blog/invideo-vs-adobe-premiere-pro-agentic-vs-timeline">
                invideo vs Premiere Pro
              </NextLink>
            </li>
            <li>
              <NextLink href="/blog/invideo-vs-capcut-social-clips-vs-filmmaking">invideo vs CapCut</NextLink>
            </li>
          </ul>
          <p>
            After you pick a tool,{' '}
            <NextLink href="/guides/how-to-review-a-video">review the cut with timestamped notes</NextLink> so feedback
            stays on the frame it belongs to.
          </p>

          <hr />

          <h2 id={getHeadingId('Frequently asked questions')}>Frequently asked questions</h2>
        </div>

        <Accordion variant="splitted" aria-label="invideo vs DaVinci Resolve FAQs" className="gap-2">
          {FAQS.map((faq) => (
            <AccordionItem key={faq.question} title={faq.question} classNames={{ title: 'text-left font-semibold' }}>
              {faq.answer}
            </AccordionItem>
          ))}
        </Accordion>

        <CTASection
          title="Go ahead and start using Kreatli for free!"
          description="After grade or generation, keep approval notes on the frame they belong to."
          primaryButtonText="Start for Free"
          primaryButtonHref="/sign-up"
        />
      </BlogPageLayout>
    </>
  );
}
